import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { dayTypes } from '../../../../../helper';
import {
  addAmenitiesV2,
  clearAmenityError,
  getBuildingAmenitiesV2,
  uploadAmenityImagesBatchV2,
  uploadAmenityWaiverDoc
} from '../../../../../redux/actions/amenities';
import { getAllBuildings } from '../../../../../redux/actions/building';
import { changeOpenedModal } from '../../../../../redux/actions/headerModal';
import { setToast } from '../../../../../redux/actions/window';
import { useTranslation } from 'react-i18next';
import { WizardModalLayout } from '../../../../../components/ModalScrollProgress';
import {
  AMENITY_ADD_WIZARD_MODAL_PROPS,
  StyledModalCardV2
} from '../../../../../components/modalV2';
import { inputSelectCustomStyles, StyledSelect } from '../../../../../components/CustomSearchBar';
import AddAmenityBasicsStepV2 from './AddAmenityBasicsStepV2';
import AddAmenityBlockingStepV2 from './AddAmenityBlockingStepV2';
import AddAmenityImagesStepV2 from './AddAmenityImagesStepV2';
import AddAmenityPaymentStepV2 from './AddAmenityPaymentStepV2';
import AddAmenityRulesStepV2 from './AddAmenityRulesStepV2';
import AddAmenityScheduleStepV2 from './AddAmenityScheduleStepV2';
import AmenityReviewDisplayV2 from './AmenityReviewDisplayV2';
import AddAmenityWizardChromeV2 from './AddAmenityWizardChromeV2';
import {
  AMENITY_WIZARD_STEP_INDICES,
  AMENITY_WIZARD_TOTAL_STEPS,
  getAmenityBlockingStepIndex,
  getAmenityImagesStepIndex,
  getAmenityPaymentStepIndex,
  getAmenityRulesStepIndex,
  getAmenitySkippedStepsNotice,
  getAmenitySubmitStepIndex,
  getAmenityWizardStepCount,
  getNextStepAfterBlocking,
  getNextStepAfterRules,
  getPreviousStepBeforeImages,
  shouldShowBlockingStep,
  shouldShowPaymentStep
} from './addAmenityWizardConfigV2';
import {
  applyWeekSelectionFromChecks,
  buildAmenitySchedulesFromDailyForm,
  formatAvailabilityDaysFromMinutes,
  formatAvailabilityHoursFromMinutes,
  getLongestContinuousAvailabilityMins,
  LONGEST_AVAILABILITY_UNBOUNDED_MINS,
  parseScheduleHour,
  selectedDaysToCodes,
  WEEK_DAY_CODES
} from './addAmenityScheduleHelpersV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../../../../components/WizardV2/wizardStylesV2';
import { AmenityWizardNote } from './addAmenityWizardStylesV2';
import {
  buildAmenityImagesBatchFormData,
  buildAmenityV2CreatePayload,
  buildAmenityWaiverDocFormData
} from './buildAmenityV2Payload';
import { buildAmenityCreateModalPayload } from './amenityCreateResultDataV2';
import { formatAmenityBuildingsSummaryFromCreate } from './amenitiesListDisplayHelpers';
import { AMENITY_CREATE_RESULT_MODAL } from './AmenityCreateResultModalV2';
import {
  amenityDefaultValues,
  createEmptyDailySchedule,
  getAmenityValidationSchema
} from './amenityFormSchemaV2';
import { disableRequirePayment, AMENITY_PAYMENT_FORM_FIELDS } from './amenityPaymentConfigV2';

const AddAmenitiesV2 = () => {
  const { t } = useTranslation();
  const prevModalRef = useRef('');

  const days = [
    { value: 'M', label: t('Monday') },
    { value: 'T', label: t('Tuesday') },
    { value: 'W', label: t('Wednesday') },
    { value: 'TH', label: t('Thursday') },
    { value: 'F', label: t('Friday') },
    { value: 'S', label: t('Saturday') },
    { value: 'SU', label: t('Sunday') }
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [amenityImages, setAmenityImages] = useState([]);
  const [waiverFile, setWaiverFile] = useState(null);

  const amenitiesLabels = {
    building: t('Building'),
    type: t('Type'),
    name: t('Amenity name'),
    available_days: t('Available days'),
    start_time: t('Available Start Time'),
    end_time: t('Available End Time'),
    max_reserve_time: t('Max reservation time'),
    min_hours: t('Min Hours'),
    max_hours: t('Max Hours'),
    min_days: t('Min Days'),
    max_days: t('Max Days'),
    allow_booking: t('Allow booking'),
    cost_per_hor: t('Cost per hour'),
    description: t('Description'),
    data: t('Data')
  };
  const dispatch = useDispatch();
  const { globalOrgId } = useSelector((state) => state.Organization);
  const { building, allBuildings } = useSelector((state) => state.building);
  const { error, amenityTypes } = useSelector((state) => state.amenities);
  const { modal } = useSelector((state) => state.headerModal);

  useEffect(() => {
    if (building?.org_id) {
      dispatch(getAllBuildings({ id: building?.org_id }));
    }
  }, [dispatch, building?.org_id]);

  const amenityImagesRef = useRef(amenityImages);
  amenityImagesRef.current = amenityImages;
  useEffect(
    () => () => {
      amenityImagesRef.current.forEach((img) => URL.revokeObjectURL(img.preview));
    },
    []
  );

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    trigger,
    getValues,
    clearErrors,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(getAmenityValidationSchema(t)),
    defaultValues: amenityDefaultValues
  });

  const allowBookingWatch = useWatch({ control, name: 'allow_booking' });
  const requiresPaymentWatch = useWatch({ control, name: 'require_payment' });
  const wizardFlags = {
    allowBooking: allowBookingWatch,
    requiresPayment: requiresPaymentWatch
  };
  const totalSteps = getAmenityWizardStepCount();
  const blockingStepIndex = getAmenityBlockingStepIndex();
  const paymentStepIndex = getAmenityPaymentStepIndex();
  const imagesStepIndex = getAmenityImagesStepIndex();
  const submitStepIndex = getAmenitySubmitStepIndex();
  const rulesStepIndex = getAmenityRulesStepIndex();
  const safeStep = Math.min(Math.max(0, wizardStep), AMENITY_WIZARD_TOTAL_STEPS - 1);
  const skippedStepsNotice = getAmenitySkippedStepsNotice(wizardFlags, t);
  const prevAllowBookingRef = useRef(allowBookingWatch);

  useEffect(() => {
    setValue('waiver_upload', waiverFile, { shouldValidate: true, shouldDirty: true });
  }, [waiverFile, setValue]);

  useEffect(() => {
    const prev = prevAllowBookingRef.current;
    prevAllowBookingRef.current = allowBookingWatch;
    if (prev !== true || allowBookingWatch !== false) return;
    setValue('allow_multi_day', false, { shouldDirty: true, shouldValidate: true });
    setValue('allow_same_day_multiple_bookings', true, {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue('slot_duration_minutes', 30, { shouldDirty: true, shouldValidate: true });
    setValue('min_reservation_hours', 0, { shouldDirty: true, shouldValidate: true });
    setValue('max_reservation_hours', null, { shouldDirty: true, shouldValidate: true });
    setValue('booking_limits_unit', 0, { shouldDirty: true, shouldValidate: true });
    setValue('min_duration_value', null, { shouldDirty: true, shouldValidate: true });
    setValue('max_duration_value', null, { shouldDirty: true, shouldValidate: true });
    setValue('max_advance_booking', 30, { shouldDirty: true, shouldValidate: true });
    setValue('require_reservation_form', false, { shouldDirty: true, shouldValidate: true });
    disableRequirePayment({ setValue, clearErrors }, { shouldValidate: true });
    setValue('require_manager_approval', false, { shouldDirty: true, shouldValidate: true });
    setValue('enable_post_booking_auto_block', false, { shouldDirty: true, shouldValidate: true });
    // Keep block duration on the same slot grid as the reset slot above.
    setValue('post_booking_auto_block_duration', 30, { shouldDirty: true, shouldValidate: true });
    setValue('post_block_description', '', { shouldDirty: true, shouldValidate: true });
    setWaiverFile(null);
  }, [allowBookingWatch, setValue, clearErrors]);

  useEffect(() => {
    if (
      wizardStep === AMENITY_WIZARD_STEP_INDICES.BLOCKING &&
      !shouldShowBlockingStep({ allowBooking: allowBookingWatch })
    ) {
      setWizardStep(AMENITY_WIZARD_STEP_INDICES.IMAGES);
      return;
    }
    if (
      wizardStep === AMENITY_WIZARD_STEP_INDICES.PAYMENT &&
      !shouldShowPaymentStep({
        allowBooking: allowBookingWatch,
        requiresPayment: requiresPaymentWatch
      })
    ) {
      setWizardStep(
        shouldShowBlockingStep({ allowBooking: allowBookingWatch })
          ? AMENITY_WIZARD_STEP_INDICES.BLOCKING
          : AMENITY_WIZARD_STEP_INDICES.IMAGES
      );
    }
  }, [allowBookingWatch, requiresPaymentWatch, wizardStep]);

  useEffect(() => {
    if (wizardStep !== paymentStepIndex) return;
    clearErrors(AMENITY_PAYMENT_FORM_FIELDS);
  }, [wizardStep, paymentStepIndex, clearErrors]);

  const getWizardFlagsFromForm = useCallback(
    () => ({
      allowBooking: getValues('allow_booking'),
      requiresPayment: getValues('require_payment')
    }),
    [getValues]
  );

  const handleApplyWeekChecks = useCallback(
    (nextChecks) => {
      applyWeekSelectionFromChecks(nextChecks, {
        setValue,
        dayOptions: days,
        t
      });
      
      WEEK_DAY_CODES.forEach((code) => {
        if (!nextChecks[code]) {
          setValue(`daily_schedule.${code}.start`, null, { shouldDirty: true, shouldValidate: false });
          setValue(`daily_schedule.${code}.end`, null, { shouldDirty: true, shouldValidate: false });
        }
      });
    },
    [setValue, days, t]
  );

  const resetWizardLocalState = useCallback(() => {
    setWizardStep(0);
    setAmenityImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.preview));
      return [];
    });
    setWaiverFile(null);
    clearErrors('waiver_upload');
  }, [clearErrors]);

  useEffect(() => {
    const opening = modal === 'addAmenityV2' && prevModalRef.current !== 'addAmenityV2';
    prevModalRef.current = modal;
    if (!opening) return;
    reset({
      ...amenityDefaultValues,
      selectedDays: { value: dayTypes.ALL_DAYS, label: t('All days') },
      daily_schedule: createEmptyDailySchedule()
    });
    resetWizardLocalState();
  }, [modal, reset, resetWizardLocalState, t]);

  const handleWizardNext = async () => {
    const flags = getWizardFlagsFromForm();

    if (wizardStep === AMENITY_WIZARD_STEP_INDICES.BASICS) {
      const ok = await trigger(['type_id', 'code'], { shouldFocus: true });
      if (!ok) return;
      setWizardStep(AMENITY_WIZARD_STEP_INDICES.SCHEDULE);
      return;
    }
    if (wizardStep === AMENITY_WIZARD_STEP_INDICES.SCHEDULE) {
      const ok = await trigger(['selectedDays', 'daily_schedule', 'slot_duration_minutes'], {
        shouldFocus: true
      });
      if (!ok) return;
      setWizardStep(AMENITY_WIZARD_STEP_INDICES.RULES);
      return;
    }
    if (wizardStep === rulesStepIndex) {
      const fields = ['allow_booking', 'require_reservation_form', 'waiver_upload'];
      if (getValues('allow_booking')) {
        fields.push('max_advance_booking');
        if (getValues('allow_multi_day')) {
          fields.push(
            'booking_limits_unit',
            'min_duration_value',
            'max_duration_value'
          );
        } else {
          fields.push('min_reservation_hours', 'max_reservation_hours');
        }
      }
      const ok = await trigger(fields, { shouldFocus: true });
      if (!ok) return;
      setWizardStep(getNextStepAfterRules(flags));
      return;
    }
    if (wizardStep === blockingStepIndex) {
      const ok = await trigger(
        ['enable_post_booking_auto_block', 'post_booking_auto_block_duration'],
        { shouldFocus: true }
      );
      if (!ok) return;
      setWizardStep(getNextStepAfterBlocking(flags));
      return;
    }
    if (wizardStep === paymentStepIndex) {
      const ok = await trigger(
        ['cost_type', 'payment_rate', 'service_charge', 'deposit'],
        { shouldFocus: true }
      );
      if (!ok) return;
      setWizardStep(imagesStepIndex);
      return;
    }
    if (wizardStep === imagesStepIndex) {
      setWizardStep(submitStepIndex);
    }
  };

  const handleWizardBack = () => {
    if (wizardStep <= 0) return;

    const flags = getWizardFlagsFromForm();

    if (wizardStep === imagesStepIndex) {
      setWizardStep(getPreviousStepBeforeImages(flags));
      return;
    }
    if (wizardStep === paymentStepIndex) {
      setWizardStep(blockingStepIndex);
      return;
    }
    if (wizardStep === blockingStepIndex) {
      setWizardStep(rulesStepIndex);
      return;
    }
    setWizardStep(wizardStep - 1);
  };

  const onSubmit = (values) => {
    setIsSubmitting(true);
    const body = buildAmenityV2CreatePayload(values, building);

    dispatch(
      addAmenitiesV2({
        body,
        success: async (res) => {
          try {
            const newAmenityId = res?.data?.new_id;
            const needsDocUpload = Boolean(
              values.require_reservation_form && waiverFile instanceof File
            );
            const needsImageUpload = Boolean(amenityImages?.length > 0);
            const needsNewIdForFollowUps = needsDocUpload || needsImageUpload;

            const runDocUpload = () => {
              if (!needsDocUpload) {
                return Promise.resolve({ role: 'doc', status: 'skipped' });
              }
              return new Promise((resolve) => {
                dispatch(
                  uploadAmenityWaiverDoc({
                    body: buildAmenityWaiverDocFormData({
                      waiverFile,
                      orgId: globalOrgId,
                      buildingId: building.id,
                      entityId: newAmenityId
                    }),
                    success: () => resolve({ role: 'doc', status: 'ok' }),
                    fail: () => resolve({ role: 'doc', status: 'failed' })
                  })
                );
              });
            };

            const runImageUpload = () => {
              if (!needsImageUpload) {
                return Promise.resolve({ role: 'img', status: 'skipped' });
              }
              return new Promise((resolve) => {
                dispatch(
                  uploadAmenityImagesBatchV2({
                    body: buildAmenityImagesBatchFormData({
                      amenityImages,
                      amenityId: newAmenityId,
                      orgId: globalOrgId,
                      buildingId: building.id
                    }),
                    success: (res) => {
                      if (res?.data?.failed?.length > 0) {
                        resolve({ role: 'img', status: 'partial' });
                        return;
                      }
                      resolve({ role: 'img', status: 'ok' });
                    },
                    fail: () => resolve({ role: 'img', status: 'failed' })
                  })
                );
              });
            };

            const resetAmenityFormToInitial = () => {
              reset({
                ...amenityDefaultValues,
                selectedDays: {},
                daily_schedule: createEmptyDailySchedule()
              });
              resetWizardLocalState();
            };

            const names = {
              amenityName: values.code ?? '',
              buildingName: building?.name ?? ''
            };

            if (
              needsNewIdForFollowUps &&
              (newAmenityId == null || newAmenityId === '')
            ) {
              resetAmenityFormToInitial();
              dispatch(getBuildingAmenitiesV2({ id: building.id }));
              dispatch(
                changeOpenedModal({
                  modal: AMENITY_CREATE_RESULT_MODAL,
                  data: {
                    variant: 'warning',
                    title: t('Amenity Created'),
                    description: t('add_amenity_created_missing_id', {
                      defaultValue:
                        'Amenity was created, but amenity ID is missing, so documents and images could not be uploaded.'
                    })
                  }
                })
              );
              return;
            }

            let docOutcome = { status: 'skipped' };
            let imgOutcome = { status: 'skipped' };
            if (needsDocUpload || needsImageUpload) {
              const outcomes = await Promise.all([runDocUpload(), runImageUpload()]);
              docOutcome = outcomes[0];
              imgOutcome = outcomes[1];
            }

            const modalPayload = buildAmenityCreateModalPayload(
              docOutcome,
              imgOutcome,
              t,
              names
            );

            resetAmenityFormToInitial();

            dispatch(getBuildingAmenitiesV2({ id: building.id }));
            dispatch(
              changeOpenedModal({
                modal: AMENITY_CREATE_RESULT_MODAL,
                data: modalPayload
              })
            );
          } finally {
            setIsSubmitting(false);
          }
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Amenities creating Failed!'),
              modal: 'errorToast'
            })
          );
          setIsSubmitting(false);
        }
      })
    );
  };

  const onClose = () => {
    dispatch(changeOpenedModal({ modal: '' }));
    reset({
      ...amenityDefaultValues,
      selectedDays: {},
      daily_schedule: createEmptyDailySchedule()
    });

    resetWizardLocalState();

    dispatch(clearAmenityError());
  };

  const footerCancel = () => {
    if (wizardStep === 0) {
      onClose();
    } else {
      handleWizardBack();
    }
  };

  const footerPrimaryLabel = () => {
    if (wizardStep < submitStepIndex) return t('Next');
    return t('Save Amenity', { defaultValue: 'Save Amenity' });
  };

  const handleFooterPrimary = async () => {
    if (wizardStep < submitStepIndex) {
      await handleWizardNext();
      return;
    }
  };

  const handleSaveAmenityClick = () => {
    if (wizardStep !== submitStepIndex) return;
    void handleSubmit(onSubmit)();
  };

  return (
    modal === 'addAmenityV2' && (
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}>
        <WizardModalLayout
          Modal={StyledModalCardV2}
          modalProps={AMENITY_ADD_WIZARD_MODAL_PROPS}
          show={modal === 'addAmenityV2'}
          closeOnBackdrop={false}
          onClose={onClose}
          width="520px"
          bodyGap={safeStep === submitStepIndex ? '24px' : '16px'}
          deps={[wizardStep, modal]}
          error={error && Object.keys(error).length > 0 ? error : undefined}
          footerActions={
            <>
              <WizardFooterButtonSecondary type="button" onClick={footerCancel}>
                {wizardStep === 0 ? t('Cancel') : t('Back')}
              </WizardFooterButtonSecondary>
              {wizardStep < submitStepIndex ? (
                <WizardFooterButtonPrimary type="button" onClick={handleFooterPrimary}>
                  {footerPrimaryLabel()}
                </WizardFooterButtonPrimary>
              ) : (
                <WizardFooterButtonPrimary
                  type="button"
                  disabled={isSubmitting || !isValid}
                  onClick={handleSaveAmenityClick}>
                  {t('Save Amenity', { defaultValue: 'Save Amenity' })}
                </WizardFooterButtonPrimary>
              )}
            </>
          }>
            {safeStep === 0 && (
              <AddAmenityWizardChromeV2
                stepIndex={0}
                displayTotalSteps={totalSteps}
                stepName={t('Details')}
                stepDescription={t('add_amenity_step_details_description', {
                  defaultValue: 'Set up the basics — name, type, and description.'
                })}>
                <AddAmenityBasicsStepV2
                  t={t}
                  building={building}
                  control={control}
                  errors={errors}
                  amenityTypes={amenityTypes}
                  setValue={setValue}
                />
              </AddAmenityWizardChromeV2>
            )}

            {safeStep === 1 && (
              <AddAmenityWizardChromeV2
                stepIndex={1}
                displayTotalSteps={totalSteps}
                stepName={t('Schedule')}
                stepDescription={t('add_amenity_step_schedule_description', {
                  defaultValue: 'Set which days and hours this amenity can be booked.'
                })}>
                <AddAmenityScheduleStepV2
                  t={t}
                  control={control}
                  errors={errors}
                  amenitiesLabels={amenitiesLabels}
                  getValues={getValues}
                  setValue={setValue}
                  onApplyWeekChecks={handleApplyWeekChecks}
                  dayOptions={days}
                  scheduleMode="singleSlot"
                />
              </AddAmenityWizardChromeV2>
            )}

            {safeStep === rulesStepIndex && (
              <AddAmenityWizardChromeV2
                stepIndex={rulesStepIndex}
                displayTotalSteps={totalSteps}
                stepName={t('Rules')}
                stepDescription={t('add_amenity_step_rules_description', {
                  defaultValue: 'Control how and when residents can make bookings.'
                })}>
                <AddAmenityRulesStepV2
                  t={t}
                  control={control}
                  errors={errors}
                  amenitiesLabels={amenitiesLabels}
                  building={building}
                  allBuildings={allBuildings}
                  waiverFile={waiverFile}
                  setWaiverFile={setWaiverFile}
                  clearErrors={clearErrors}
                  setValue={setValue}
                  trigger={trigger}
                />
              </AddAmenityWizardChromeV2>
            )}

            {safeStep === blockingStepIndex && shouldShowBlockingStep(wizardFlags) && (
              <AddAmenityWizardChromeV2
                stepIndex={blockingStepIndex}
                displayTotalSteps={totalSteps}
                stepName={t('Blocking')}
                stepDescription={t('add_amenity_step_blocking_description', {
                  defaultValue: 'Block a time after certain amenities are used (i.e., cleaning.)'
                })}>
                <AddAmenityBlockingStepV2
                  t={t}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  trigger={trigger}
                  clearErrors={clearErrors}
                />
              </AddAmenityWizardChromeV2>
            )}

            {safeStep === paymentStepIndex && shouldShowPaymentStep(wizardFlags) && (
              <AddAmenityWizardChromeV2
                stepIndex={paymentStepIndex}
                displayTotalSteps={totalSteps}
                stepName={t('Payment')}
                stepDescription={t('add_amenity_step_payment_description', {
                  defaultValue: 'Set a booking rate, and add optional service fee and deposit.'
                })}>
                <AddAmenityPaymentStepV2
                  t={t}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                />
              </AddAmenityWizardChromeV2>
            )}

            {safeStep === imagesStepIndex && (
              <AddAmenityWizardChromeV2
                stepIndex={imagesStepIndex}
                displayTotalSteps={totalSteps}
                stepName={t('Images')}
                stepDescription={t('add_amenity_step_images_description', {
                  defaultValue: 'Add amenity images so residents can view on the resident app.'
                })}
                stepNotice={
                  skippedStepsNotice ? (
                    <AmenityWizardNote>{skippedStepsNotice}</AmenityWizardNote>
                  ) : null
                }>
                <AddAmenityImagesStepV2
                  t={t}
                  amenityImages={amenityImages}
                  setAmenityImages={setAmenityImages}
                  building={building}
                />
              </AddAmenityWizardChromeV2>
            )}

            {safeStep === submitStepIndex && (
              <AddAmenityWizardChromeV2
                stepIndex={submitStepIndex}
                displayTotalSteps={totalSteps}
                stepName={t('Review')}
                stepDescription={t('add_amenity_step_review_description', {
                  defaultValue: 'Review all settings before saving the amenity.'
                })}>
                <AmenityReviewDisplayV2
                  t={t}
                  values={getValues()}
                  amenityTypes={amenityTypes}
                  amenityImages={amenityImages}
                  waiverFile={waiverFile}
                  buildingsSummary={formatAmenityBuildingsSummaryFromCreate(
                    building?.name,
                    getValues().building_ids
                  )}
                />
              </AddAmenityWizardChromeV2>
            )}
        </WizardModalLayout>
      </form>
    )
  );
};

export default AddAmenitiesV2;
